import React from "react";

/**
 * ArchitectureDiagram — J.A.R.V.I.S.-style system map. Pops up when the owner
 * asks "show me how you work". Renders 4 columns: Reasoning Core, Orchestrator
 * + Agents, Channels, Tools — wired together with glowing connectors.
 *
 * Owner asked: "the diagram map that's all clickable now, isn't it?" — every
 * pill now triggers a relevant voice command when clicked. Agents open
 * SwarmChat with that agent's name pre-loaded, tools fire the relevant
 * "open X" command, channels open the matching settings tab, models route
 * to model switching.
 */

// Mapping from pill label → voice command we fire on click. Lowercase
// matching against the start of the label is fine — keeps the map small.
const COMMAND_MAP = {
  // Agents — open SwarmChat focused on this agent
  "tom":   "ask Tom what to focus on next",
  "sarah": "ask Sarah what to focus on next",
  "eva":   "ask Eva what to focus on next",
  "scout": "ask Scout what to focus on next",
  "dobby": "ask Dobby what to focus on next",
  // Channels
  "voice":      "show me what voices I can choose from",
  "swarmchat":  "open the swarm chat",
  "slack":      "open settings on the integrations tab",
  "imessage":   "open settings on the integrations tab",
  // Tools / connections (broad match — first word wins)
  "capital":    "show me my live capital P&L",
  "stripe":     "what's in my Stripe right now",
  "gmail":      "open gmail",
  "calendar":   "open google calendar",
  "tavily":     "search the web for the latest news",
  "elevenlabs": "show me what voices I can choose from",
  "instagram":  "open the social composer for instagram",
  "tiktok":     "open the social composer for tiktok",
  "youtube":    "show me my recent YouTube videos",
  "ring":       "show me the doorbell",
  "shelly":     "what plugs have I got registered",
  // Models — model switching
  "gpt":        "open settings on the models tab",
  "claude":     "open settings on the models tab",
  "gemini":     "open settings on the models tab",
};

function pillCommand(label) {
  const lc = (label || "").toLowerCase();
  // Try exact key first, then prefix match
  if (COMMAND_MAP[lc]) return COMMAND_MAP[lc];
  for (const k of Object.keys(COMMAND_MAP)) {
    if (lc.startsWith(k)) return COMMAND_MAP[k];
  }
  return null;
}

function Pill({ label, isActive, onSend }) {
  const cmd = pillCommand(label);
  const clickable = !!cmd && !!onSend;
  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={() => clickable && onSend(cmd)}
      data-testid={`arch-pill-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      title={clickable ? `Click to: "${cmd}"` : label}
      className={`rc-arch-pill text-left w-full ${isActive ? "is-active" : ""} ${clickable ? "rc-arch-pill--clickable" : ""}`}
    >
      ◆ {label}
    </button>
  );
}

export default function ArchitectureDiagram({ data, onSend }) {
  if (!data) return null;
  const core = data.reasoning_core || {};
  const orch = data.orchestrator || {};
  const channels = data.channels || [];
  const tools = data.tools || [];

  return (
    <div className="rc-arch" data-testid="architecture-diagram">
      {/* Top row — Reasoning Core */}
      <div className="rc-arch-top">
        <div className="rc-arch-box rc-arch-core">
          <div className="rc-label text-[9px] mb-1">◉ REASONING CORE</div>
          <div className="font-display text-xs rc-glow-text mb-2">{(core.label || "Reasoning Core").toUpperCase()}</div>
          <div className="space-y-1">
            {(core.models || []).map((m, i) => (
              <Pill key={i} label={m} isActive={i === 0} onSend={onSend} />
            ))}
          </div>
        </div>
        <div className="rc-arch-line vert" />
      </div>

      {/* Middle row — Channels | Orchestrator+Agents | Tools */}
      <div className="rc-arch-mid">
        {/* Channels */}
        <div className="rc-arch-col">
          <div className="rc-arch-box">
            <div className="rc-label text-[9px] mb-1">◉ CHANNELS</div>
            <div className="text-[8px] text-[var(--rc-text-dim)] mb-2">INPUT SURFACES</div>
            <div className="space-y-1.5">
              {channels.map((c, i) => (
                <Pill key={i} label={c.name.toUpperCase()} onSend={onSend} />
              ))}
            </div>
          </div>
        </div>

        <div className="rc-arch-line horiz" />

        {/* Orchestrator + agents */}
        <div className="rc-arch-col-wide">
          <div className="rc-arch-box rc-arch-orchestrator">
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-display text-lg rc-glow-text tracking-widest">◉ {(orch.label || "R.A.W. Company").toUpperCase()}</div>
                <div className="text-[8px] text-[var(--rc-text-dim)] tracking-widest mt-0.5">{(orch.subtitle || "AGENT ORCHESTRATOR").toUpperCase()}</div>
              </div>
              <div className="rc-label text-[8px] opacity-70">AGENT MESH</div>
            </div>
            <div className="space-y-1.5">
              {(orch.agents || []).map((a, i) => {
                const cmd = pillCommand(a.name);
                const clickable = !!cmd && !!onSend;
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={!clickable}
                    onClick={() => clickable && onSend(cmd)}
                    title={clickable ? `Click to ask ${a.name}` : a.name}
                    data-testid={`arch-agent-${a.name.toLowerCase()}`}
                    className={`rc-arch-agent w-full text-left ${clickable ? "rc-arch-pill--clickable" : ""}`}
                  >
                    <div className="rc-arch-bullet" />
                    <div className="font-display text-xs flex-1">{a.name.toUpperCase()}</div>
                    <div className="text-[10px] text-[var(--rc-text-dim)] tracking-wide">{a.role.toUpperCase()}</div>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-between text-[8px] rc-label opacity-60 mt-3 pt-2 border-t border-[var(--rc-gold-dim)]">
              <span>{(orch.agents || []).length} AGENTS · SHARED CONTEXT</span>
              <span>STATE · IDLE</span>
            </div>
          </div>
        </div>

        <div className="rc-arch-line horiz" />

        {/* Tools */}
        <div className="rc-arch-col">
          <div className="rc-arch-box">
            <div className="rc-label text-[9px] mb-1">◉ CONNECTIONS</div>
            <div className="text-[8px] text-[var(--rc-text-dim)] mb-2">TOOLS · APIs</div>
            <div className="space-y-1.5 max-h-[42vh] overflow-y-auto pr-1">
              {tools.map((tl, i) => (
                <Pill key={i} label={tl.name.toUpperCase()} onSend={onSend} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
